import { z } from 'zod';

/**
 * BỐ CỤC BẢNG "DANH MỤC VẬT TƯ THIẾT BỊ" — chỉnh theo từng báo giá trước khi phát hành (ẩn cột Ghi chú,
 * kéo độ rộng cột, độ cao dòng). Lưu trong dữ liệu nhập của bản nháp, chốt vào snapshot khi phát hành.
 * Mẫu đọc qua biến `layout.items` (mẫu cũ không dùng biến này vẫn chạy như trước).
 */
export const ITEM_TABLE_COLUMNS = [
  'index',
  'name',
  'specs',
  'manufacturer',
  'quantity',
  'unit',
  'image',
  'unitPrice',
  'amount',
  'note',
] as const;
export type ItemTableColumn = (typeof ITEM_TABLE_COLUMNS)[number];

export const ITEM_TABLE_COLUMN_LABELS: Record<ItemTableColumn, string> = {
  index: 'STT',
  name: 'Tên sản phẩm',
  specs: 'Thông số kỹ thuật',
  manufacturer: 'Hãng sản xuất',
  quantity: 'SL',
  unit: 'ĐVT',
  image: 'Hình ảnh',
  unitPrice: 'Đơn giá',
  amount: 'Thành tiền',
  note: 'Ghi chú',
};

/** Độ rộng mặc định (%) — bảng có đơn giá (gói tính theo vật tư) và bảng không có đơn giá. */
export const ITEM_TABLE_DEFAULT_WIDTHS: Record<'withPrices' | 'withoutPrices', Partial<Record<ItemTableColumn, number>>> = {
  withPrices: { index: 4, name: 17, specs: 18, manufacturer: 9, quantity: 5, unit: 5, image: 9, unitPrice: 12, amount: 13, note: 8 },
  withoutPrices: { index: 5, name: 24, specs: 28, manufacturer: 11, quantity: 6, unit: 6, image: 10, note: 10 },
};

export const ITEM_TABLE_MIN_WIDTH = 3;
export const ITEM_ROW_HEIGHT_RANGE = { min: 8, max: 80 } as const;

export const itemsLayoutSchema = z.object({
  hideNote: z.boolean().default(false),
  /** Độ rộng tương đối (%) theo cột; cột không có ⇒ mặc định. Khi hiển thị được chuẩn hoá về tổng 100%. */
  widths: z.partialRecord(z.enum(ITEM_TABLE_COLUMNS), z.number().min(ITEM_TABLE_MIN_WIDTH).max(90)).default({}),
  /** Chiều cao tối thiểu mỗi dòng (mm); null = tự động theo nội dung */
  rowHeightMm: z.number().min(ITEM_ROW_HEIGHT_RANGE.min).max(ITEM_ROW_HEIGHT_RANGE.max).nullable().default(null),
});
export type ItemsLayout = z.infer<typeof itemsLayoutSchema>;

export interface ResolvedItemsLayout {
  columns: ItemTableColumn[];
  /** % theo cột hiển thị, tổng = 100 */
  widths: Partial<Record<ItemTableColumn, number>>;
  showNote: boolean;
  rowHeightMm: number | null;
}

/**
 * Cột hiển thị + độ rộng chuẩn hoá. Ẩn cột Ghi chú mà chưa chỉnh độ rộng ảnh ⇒ phần rộng của Ghi chú dồn cho cột
 * Hình ảnh (mục đích chính của việc ẩn: ảnh vật tư to hơn).
 */
export function resolveItemsLayout(layout: Partial<ItemsLayout> | null | undefined, showItemPrices: boolean): ResolvedItemsLayout {
  const defaults = ITEM_TABLE_DEFAULT_WIDTHS[showItemPrices ? 'withPrices' : 'withoutPrices'];
  const showNote = !layout?.hideNote;
  const custom = layout?.widths ?? {};
  const columns = ITEM_TABLE_COLUMNS.filter((c) => defaults[c] != null && (c !== 'note' || showNote));
  const raw = Object.fromEntries(columns.map((c) => [c, custom[c] ?? defaults[c]!])) as Record<ItemTableColumn, number>;
  if (!showNote && custom.image == null) raw.image += defaults.note!;
  const total = columns.reduce((t, c) => t + raw[c], 0);
  const widths: Partial<Record<ItemTableColumn, number>> = {};
  for (const c of columns) widths[c] = Math.round((raw[c] / total) * 10000) / 100;
  return { columns, widths, showNote, rowHeightMm: layout?.rowHeightMm ?? null };
}
